import React, { useCallback, useEffect, useRef, useState } from "react";
import { runsApi } from "../../api/endpoints";

/**
 * ProvenancePopover — deterministic provenance affordance.
 *
 * Two modes, one component:
 *   • mode="process" (default) — fetches
 *     `runs/{runId}/processes/{pid}/provenance` and looks up
 *     `by_field[field]`.
 *   • mode="finding"           — fetches
 *     `runs/{runId}/findings/{fid}/provenance` and looks up
 *     `by_field[field]`. Use this next to a finding's `facts_used` entries
 *     so the popover shows the exact source rows the engine consumed for
 *     that fact.
 *
 * Renders the seven canonical fields per row:
 *   source_file · sheet · row · column · raw_value · normalized_value ·
 *   normalization_method
 *
 * If the backend returns no entry (JSON-ingest / seed runs or non-process
 * scoped facts), the popover renders the literal string
 * "No source provenance." — the UI never fabricates workbook/sheet/row.
 *
 * Keyboard & focus model (in-house — no external primitive):
 *   • The trigger is a native <button>; Enter/Space open the popover both
 *     via native activation and via an explicit onKeyDown for robustness
 *     under synthetic-event dispatch.
 *   • On open we snapshot the previously-focused element (the trigger) and
 *     move focus to the close button inside the panel.
 *   • Tab / Shift-Tab wrap-cycle within the panel's focusable elements
 *     (focus trap). Focus can never escape to the underlying page while
 *     the panel is open.
 *   • Escape, outside-click, and close-button click all route through the
 *     same closePanel() which restores focus to the trigger.
 */
export function ProvenancePopover({
  workspaceId,
  runId,
  // Process mode
  processId,
  // Finding mode
  findingId,
  // Common
  field,
  fallbackLabel = "source",
  mode = "process",
  ariaLabel,
}) {
  const [open, setOpen] = useState(false);
  const [prov, setProv] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const closeRef = useRef(null);
  // Snapshot of the DOM element that had focus immediately before the
  // panel opened. On close we always restore focus to it (usually the
  // trigger, but we save what we saw rather than assume).
  const previousFocusRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const p =
        mode === "finding"
          ? await runsApi.findingProvenance(workspaceId, runId, findingId)
          : await runsApi.processProvenance(workspaceId, runId, processId);
      setProv(p);
    } catch (e) {
      setErr(e);
    } finally {
      setLoading(false);
    }
  }, [mode, workspaceId, runId, processId, findingId]);

  // Latest values of load/prov/loading so open-transition side effects can
  // read them without re-firing on unrelated re-renders.
  const loadRef = useRef(load);
  loadRef.current = load;
  const provRef = useRef(prov);
  provRef.current = prov;
  const loadingRef = useRef(loading);
  loadingRef.current = loading;

  // Single, symmetric activator for Enter and Space (WCAG 2.1 SC 2.1.1).
  // We *preventDefault* on both keys so the browser's implicit click never
  // fires alongside our toggle. Functional setState sidesteps any stale-
  // closure races between real-keyboard keydown and any subsequent event
  // dispatched by native activation.
  const handleTriggerKeyDown = useCallback((e) => {
    if (e.key === "Enter" || e.key === " " || e.key === "Spacebar") {
      e.preventDefault();
      e.stopPropagation();
      setOpen((prev) => !prev);
    }
  }, []);

  // Programmatic open/close paths for mouse click and the internal close
  // button. Mirror the same setOpen(prev => !prev) shape so we never rely
  // on a stale `open` closure.
  const openPanel = useCallback(() => setOpen(true), []);
  const closePanel = useCallback(() => setOpen(false), []);

  // Side effects that must run WHEN `open` transitions to true:
  //  • snapshot the previously-focused element (so we can restore on close)
  //  • kick off the lazy fetch, once.
  useEffect(() => {
    if (!open) return;
    previousFocusRef.current =
      (typeof document !== "undefined" && document.activeElement) || null;
    if (!provRef.current && !loadingRef.current) {
      loadRef.current();
    }
  }, [open]);

  // Side effects that must run WHEN `open` transitions to false:
  //  • return focus to the trigger (or the previously-focused element).
  // Skipped on the initial render (open starts false) via wasOpenRef.
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (open) {
      wasOpenRef.current = true;
      return;
    }
    if (!wasOpenRef.current) return;
    wasOpenRef.current = false;
    const restoreTarget =
      triggerRef.current ||
      (previousFocusRef.current &&
      typeof document !== "undefined" &&
      document.contains(previousFocusRef.current)
        ? previousFocusRef.current
        : null);
    if (restoreTarget && typeof restoreTarget.focus === "function") {
      restoreTarget.focus();
    }
  }, [open]);

  // Escape closes from anywhere while open. Outside-click closes when the
  // pointer down happens outside the panel (and outside the trigger).
  useEffect(() => {
    if (!open) return undefined;

    function onDocKeyDown(e) {
      if (e.key === "Escape") {
        e.stopPropagation();
        closePanel();
        return;
      }
      if (e.key === "Tab") {
        // Focus-trap: cycle within the panel's focusable elements.
        const panel = panelRef.current;
        if (!panel) return;
        const focusables = getFocusable(panel);
        if (focusables.length === 0) {
          e.preventDefault();
          return;
        }
        const active = document.activeElement;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        // If focus is currently outside the panel — pull it back in.
        if (!panel.contains(active)) {
          e.preventDefault();
          first.focus();
          return;
        }
        if (e.shiftKey && active === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    function onDocPointerDown(e) {
      const panel = panelRef.current;
      const trigger = triggerRef.current;
      if (!panel) return;
      const target = e.target;
      if (panel.contains(target) || (trigger && trigger.contains(target))) {
        return; // click landed inside; ignore.
      }
      // Prevent the outside target from stealing focus on the ensuing
      // mousedown default (focus-shift) — closePanel restores focus to
      // the trigger below, per the contract.
      if (e.type === "mousedown" && typeof e.preventDefault === "function") {
        e.preventDefault();
      }
      closePanel();
    }

    document.addEventListener("keydown", onDocKeyDown, true);
    document.addEventListener("mousedown", onDocPointerDown, true);
    document.addEventListener("touchstart", onDocPointerDown, true);

    // Move focus into the close button on open. Use rAF so the panel is
    // painted before we try to focus (jsdom fires effects synchronously so
    // this still works there).
    const raf =
      typeof window !== "undefined" && window.requestAnimationFrame
        ? window.requestAnimationFrame
        : (fn) => setTimeout(fn, 0);
    const cancel =
      typeof window !== "undefined" && window.cancelAnimationFrame
        ? window.cancelAnimationFrame
        : clearTimeout;
    const handle = raf(() => {
      if (closeRef.current) closeRef.current.focus();
    });

    return () => {
      document.removeEventListener("keydown", onDocKeyDown, true);
      document.removeEventListener("mousedown", onDocPointerDown, true);
      document.removeEventListener("touchstart", onDocPointerDown, true);
      cancel(handle);
    };
  }, [open, closePanel]);

  const rows = prov?.by_field?.[field] || [];
  const hasRows = rows.length > 0;
  const label = ariaLabel || `Show provenance for ${field}`;
  const safeField = String(field).replace(/[^A-Za-z0-9_.-]/g, "_");

  return (
    <span className="relative inline-block">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        onKeyDown={handleTriggerKeyDown}
        data-testid={`prov-toggle-${safeField}`}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={label}
        title={label}
        className="ml-1 inline-flex items-center gap-1 rounded border border-slate-200 bg-white px-1.5 py-0.5 text-[10px] font-mono text-slate-600 hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
      >
        <ProvIcon />
        <span>{fallbackLabel}</span>
      </button>
      {open && (
        <span
          ref={panelRef}
          role="dialog"
          aria-modal="true"
          aria-label={label}
          data-testid={`prov-panel-${safeField}`}
          className="absolute left-0 top-full z-40 mt-1 w-96 rounded border border-slate-200 bg-white p-3 shadow-lg text-left"
        >
          <div className="mb-2 flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-wider text-slate-500">
              Provenance · {field}
            </div>
            <button
              ref={closeRef}
              type="button"
              onClick={closePanel}
              className="text-[11px] text-slate-500 hover:text-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 rounded px-1"
              data-testid={`prov-close-${safeField}`}
              aria-label="Close provenance popover"
            >
              Close
            </button>
          </div>
          {loading && (
            <div
              className="text-xs text-slate-500"
              data-testid={`prov-loading-${safeField}`}
            >
              Loading provenance…
            </div>
          )}
          {err && (
            <div
              className="text-xs text-red-700"
              data-testid={`prov-error-${safeField}`}
            >
              Failed to load provenance ({err.code || "ERROR"}).
            </div>
          )}
          {!loading && !err && !hasRows && (
            <div
              className="text-xs italic text-slate-600"
              data-testid={`prov-none-${safeField}`}
            >
              No source provenance.
            </div>
          )}
          {hasRows && (
            <ul
              className="space-y-2"
              data-testid={`prov-rows-${safeField}`}
            >
              {rows.map((r, i) => (
                <ProvenanceRow
                  key={i}
                  row={r}
                  testIdPrefix={`prov-row-${safeField}-${i}`}
                />
              ))}
            </ul>
          )}
        </span>
      )}
    </span>
  );
}

/**
 * Return the tab-order-focusable elements inside `root`. We intentionally
 * keep the selector list small — this component's panel only contains
 * buttons and (potentially) links.
 */
function getFocusable(root) {
  if (!root) return [];
  const nodes = root.querySelectorAll(
    [
      'a[href]:not([tabindex="-1"])',
      'button:not([disabled]):not([tabindex="-1"])',
      'input:not([disabled]):not([tabindex="-1"])',
      'select:not([disabled]):not([tabindex="-1"])',
      'textarea:not([disabled]):not([tabindex="-1"])',
      '[tabindex]:not([tabindex="-1"])',
    ].join(","),
  );
  return Array.from(nodes).filter((n) => !n.hasAttribute("aria-hidden"));
}

function ProvenanceRow({ row, testIdPrefix }) {
  return (
    <li className="text-xs text-slate-700 border-b border-slate-100 pb-1 last:border-0 last:pb-0">
      <dl className="grid grid-cols-[max-content_1fr] gap-x-2 gap-y-0.5">
        <Term label="source_file" testId={`${testIdPrefix}-source-file`}>
          {row.source_file || "—"}
        </Term>
        <Term label="sheet" testId={`${testIdPrefix}-sheet`}>
          {row.sheet || "—"}
        </Term>
        <Term label="row" testId={`${testIdPrefix}-row`}>
          {row.row === null || row.row === undefined ? "—" : String(row.row)}
        </Term>
        <Term label="column" testId={`${testIdPrefix}-column`}>
          {row.column || "—"}
        </Term>
        <Term label="raw_value" testId={`${testIdPrefix}-raw`}>
          <code className="font-mono">
            {row.raw_value === null || row.raw_value === undefined
              ? "—"
              : String(row.raw_value)}
          </code>
        </Term>
        <Term label="normalized_value" testId={`${testIdPrefix}-normalized`}>
          <code className="font-mono">
            {row.normalized_value === null || row.normalized_value === undefined
              ? "—"
              : String(row.normalized_value)}
          </code>
        </Term>
        <Term
          label="normalization_method"
          testId={`${testIdPrefix}-method`}
        >
          {row.method || "—"}
        </Term>
      </dl>
    </li>
  );
}

function Term({ label, children, testId }) {
  return (
    <>
      <dt className="text-[10px] uppercase tracking-wider text-slate-500">
        {label}
      </dt>
      <dd className="text-slate-800" data-testid={testId}>
        {children}
      </dd>
    </>
  );
}

function ProvIcon() {
  // Small "info source" indicator, kept as an inline SVG to avoid extra imports.
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      width="9"
      height="9"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="8.01" />
      <line x1="11" y1="12" x2="12" y2="12" />
      <line x1="12" y1="12" x2="12" y2="16" />
    </svg>
  );
}
